import { createFileRoute, useRouter } from "@tanstack/react-router"
import { useServerFn } from "@tanstack/react-start"
import { useState } from "react"

import config from "@/config"

import { BrandingPrompt } from "../components/branding-prompt/branding-prompt"
import { InvoiceEditor } from "../components/invoice-editor/invoice-editor"
import { PageError, PageLoading } from "../components/page-state/page-state"
import { SayIt } from "../components/say-it/say-it"
import { applyDraftProposal, emptyForm, type InvoiceForm } from "../editor-model"
import {
  createCustomerAction,
  createInvoiceAction,
  loadBranding,
  loadCustomers,
  loadInvoiceUsage,
  loadSellerProfile,
  proposeInvoiceDraftAction,
  updateBrandingAction,
  updateSellerProfileAction,
} from "../rpc"
import { sydneyToday } from "../status"

export const Route = createFileRoute("/_app-shell/invoices/new")({
  loader: async () => {
    const [seller, customers, usage, brandingView] = await Promise.all([
      loadSellerProfile(),
      loadCustomers(),
      loadInvoiceUsage(),
      // the template switch is optional: the form still opens when branding cannot be loaded
      loadBranding().catch(() => null),
    ])
    return {
      seller,
      customers,
      usage,
      branding: brandingView?.branding ?? null,
      today: sydneyToday(new Date()),
    }
  },
  head: () => ({
    meta: [
      { title: `New invoice - ${config.core.projectName}` },
      { name: "robots", content: "noindex" },
    ],
  }),
  pendingComponent: () => <PageLoading title="New invoice" label="Loading the invoice form" />,
  errorComponent: ({ error }) => {
    const router = useRouter()
    return (
      <PageError
        title="New invoice"
        message={error.message || "The invoice form could not be loaded."}
        onRetry={() => router.invalidate()}
      />
    )
  },
  component: InvoiceNewRoute,
})

function InvoiceNewRoute() {
  const { seller, customers, usage, branding, today } = Route.useLoaderData()
  const router = useRouter()
  const createInvoice = useServerFn(createInvoiceAction)
  const createCustomer = useServerFn(createCustomerAction)
  const saveSeller = useServerFn(updateSellerProfileAction)
  const propose = useServerFn(proposeInvoiceDraftAction)
  const saveBranding = useServerFn(updateBrandingAction)
  // A proposal replaces the form (the editor remounts on `version`); the user still saves it.
  const [voice, setVoice] = useState<{
    version: number
    form: InvoiceForm
    candidates: string[]
  } | null>(null)

  return (
    <>
      <div className="mx-auto w-full max-w-7xl space-y-4 px-6 pt-8">
        <BrandingPrompt />
        <SayIt
          onPropose={(text) => propose({ data: { text } })}
          onProposal={(proposal) =>
            setVoice((current) => ({
              version: (current?.version ?? 0) + 1,
              candidates: proposal.customer_candidate_ids,
              form: applyDraftProposal(
                emptyForm({ today, termsDays: seller.default_payment_terms_days }),
                proposal,
                customers,
                seller.default_payment_terms_days,
              ),
            }))
          }
        />
      </div>
      <InvoiceEditor
        key={voice?.version ?? 0}
        initialForm={voice?.form}
        requireCustomer={voice !== null}
        candidateCustomerIds={voice?.candidates}
        seller={seller}
        customers={customers}
        usage={usage}
        today={today}
        onSave={(request) => createInvoice({ data: request })}
        onSaved={(invoice, intent) =>
          router.navigate({
            to: "/invoices/$id",
            params: { id: invoice.id },
            search: intent === "send" ? { send: true } : {},
          })
        }
        onCreateCustomer={(request) => createCustomer({ data: request })}
        branding={
          branding
            ? { default_template: branding.default_template, brand_color: branding.brand_color }
            : undefined
        }
        onSaveDefaultTemplate={async (template) => {
          const result = await saveBranding({ data: { default_template: template } })
          if (result.ok) void router.invalidate()
          return result
        }}
        onSaveSeller={async (request) => {
          const result = await saveSeller({ data: request })
          if (result.ok) void router.invalidate()
          return result
        }}
      />
    </>
  )
}
